import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useUrlState } from "@/hooks/useUrlState";
import { toast } from "sonner";
import { api, errDetail, fmtIST, fmtISTTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { TestBadge, ApptBadge, CalSyncBadge, IntentBadge, EmptyState, ErrorState } from "@/components/common";
import { ConsultationActions } from "@/components/ConsultationActions";
import { TagsEditor } from "@/components/TagsEditor";
import { CalendarClock, UserPlus } from "lucide-react";
import { useSlotPicker } from "@/hooks/useSlotPicker";
import { useIncludeTest } from "@/hooks/useIncludeTest";
import { IncludeTestToggle } from "@/components/IncludeTestToggle";
import { SlotPickerView } from "@/components/SlotPickerView";

// R14 pass 2 - the Completed tab can be narrowed to the consultation's OWN recorded commercial outcome. The option list is fetched from the backend
// registry (GET /appointments/outcome-filters); this static copy is only the fallback if that call fails, and is identical in values and labels.
const OUTCOME_FILTER_FALLBACK = [
  { value: "all", label: "All outcomes" }, { value: "signup", label: "Signup" }, { value: "agreed_to_proceed", label: "Agreed to Proceed" },
  { value: "signup_or_agreed_to_proceed", label: "Signup / Agreed to Proceed" }, { value: "not_ready", label: "Not Ready" },
  { value: "lost", label: "Lost" }, { value: "proceed_for_admission", label: "Proceed for Admission" }, { value: "not_recorded", label: "Not recorded" },
];
const OUTCOME_QUEUES = ["completed", "all", "my"];     // the queues where a recorded outcome column is meaningful

const OUTCOME_TONE = {
  signup: "border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]",
  agreed_to_proceed: "border-[hsl(var(--info))]/40 bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]",
  not_ready: "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]",
  lost: "border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))]",
  proceed_for_admission: "border-[hsl(var(--info))]/40 bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]",
};

/** The consultation's own recorded outcome - readable, never guessed ("Not recorded" for a historic row with no outcome). */
export const OutcomeBadge = ({ outcome, testId }) => {
  const o = outcome || { value: null, label: "Not recorded", recorded: false };
  const hint = o.value === "agreed_to_proceed"
    ? "Agreed to Proceed is recorded on this consultation. It is not a commercial Signup and does not by itself mean a Case was activated."
    : o.recorded ? `Recorded on this consultation: ${o.label}` : "No commercial outcome was recorded on this consultation.";
  return (
    <Badge variant="outline" title={hint} data-testid={testId}
      className={`text-[11px] ${o.recorded ? (OUTCOME_TONE[o.value] || "") : "text-muted-foreground border-dashed"}`}>{o.label}</Badge>
  );
};

const QUEUES = [
  { key: "my", label: "My Consultations" },
  { key: "today", label: "Today" },
  { key: "upcoming", label: "Upcoming" },
  { key: "needs_outcome", label: "Needs outcome" },
  { key: "completed", label: "Completed" },
  { key: "no_show", label: "No Show" },
  { key: "cancelled", label: "Cancelled" },
  { key: "unassigned", label: "Unassigned" },
  { key: "all", label: "All" },
];

/* Manual internal consultation creation: find customer -> opportunity ->
   type -> date -> atomic slot booking (same engine as self-booking). */
const NewConsultationDialog = ({ open, setOpen, onBooked }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [contact, setContact] = useState(null);
  const [opps, setOpps] = useState([]);
  const [oppId, setOppId] = useState("");
  const [types, setTypes] = useState([]);
  const [typeId, setTypeId] = useState("");
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);
  const savingGuard = useRef(false);        // a double click / Enter while a booking is in flight must not post it twice
  const [mode, setMode] = useState("existing"); // existing | new customer
  const [nc, setNc] = useState({ name: "", phone: "", email: "", product_code: "", counsellor_user_id: "", notes: "" });
  const [ncDate, setNcDate] = useState("");
  const [products, setProducts] = useState([]);
  const [counsellors, setCounsellors] = useState([]);
  const ncTypeId = types.find((t) => t.product_code === nc.product_code)?.appointment_type_id || "";
  // R15 U1: each slot belongs to the exact (type, date, opportunity / product / consultant) it was fetched for; changing any of them invalidates it immediately, late responses are
  // ignored, and the status says why nothing can be picked (closed / not permitted / could not load).
  const picker = useSlotPicker({
    key: open && mode === "existing" && typeId && date ? `${typeId}|${date}|${oppId || ""}` : null, date,
    load: async () => (await api.get(`/appointments/slots?appointment_type_id=${typeId}&date_str=${date}`)).data,
  });
  const ncPicker = useSlotPicker({
    key: open && mode === "new" && ncTypeId && ncDate ? `${ncTypeId}|${ncDate}|${nc.product_code}|${nc.counsellor_user_id}` : null, date: ncDate,
    load: async () => (await api.get(`/appointments/slots?appointment_type_id=${ncTypeId}&date_str=${ncDate}`)).data,
  });

  useEffect(() => {
    if (open && mode === "new") {
      api.get("/products").then((r) => {
        const bookable = (r.data || []).filter((p) => !p.parent_code);
        setProducts(bookable);
        if (bookable[0] && !nc.product_code) setNc((p) => ({ ...p, product_code: bookable[0].code }));
      });
      api.get("/users").then((r) => setCounsellors(r.data.users.filter((u) => u.is_active && ["counsellor", "supervisor", "manager", "admin"].includes(u.role)))).catch(() => {});
    }
  }, [open, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const bookNewCustomer = async () => {
    if (!ncPicker.slot || savingGuard.current) return;
    savingGuard.current = true;
    setSaving(true);
    try {
      const r = await api.post("/consultations/new-with-customer", {
        name: nc.name, phone: nc.phone, email: nc.email || null,
        product_code: nc.product_code, counsellor_user_id: nc.counsellor_user_id,
        start_iso: ncPicker.slot, notes: nc.notes || null,
      });
      toast.success(`Booked (${r.data.dedup_result}). Customer ${r.data.customer_id || ""}`);
      if (r.data.whatsapp_deeplink) window.open(r.data.whatsapp_deeplink, "_blank");
      setOpen(false); setNc({ name: "", phone: "", email: "", product_code: "", counsellor_user_id: "", notes: "" });
      setNcDate("");
      onBooked();
    } catch (e) { toast.error(errDetail(e)); ncPicker.reload(); } finally { setSaving(false); savingGuard.current = false; }
  };

  useEffect(() => {
    if (open) api.get("/appointments/types").then((r) => {
      const active = (r.data || []).filter((t) => t.active);
      setTypes(active);
      if (active[0]) setTypeId(active[0].appointment_type_id);
    });
  }, [open]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data)), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    if (!contact) return;
    api.get(`/contacts/${contact.contact_id}/full`).then((r) => {
      const active = r.data.opportunities.filter((o) => o.lifecycle_status === "active");
      setOpps(active);
      if (active[0]) setOppId(active[0].opportunity_id);
    });
  }, [contact]);

  const book = async () => {
    if (!picker.slot || savingGuard.current) return;
    savingGuard.current = true;
    setSaving(true);
    try {
      await api.post("/appointments", { opportunity_id: oppId, appointment_type_id: typeId, start: picker.slot });
      toast.success("Consultation booked");
      setOpen(false); setContact(null); setQ(""); setDate("");
      onBooked();
    } catch (e) { toast.error(errDetail(e)); picker.reload(); } finally { setSaving(false); savingGuard.current = false; }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">New consultation / meeting</DialogTitle></DialogHeader>
        <div className="flex gap-1 rounded-md border p-1 w-fit">
          <Button size="sm" variant={mode === "existing" ? "default" : "ghost"} className="h-7 text-xs" data-testid="newconsult-mode-existing" onClick={() => setMode("existing")}>Existing customer</Button>
          <Button size="sm" variant={mode === "new" ? "default" : "ghost"} className="h-7 text-xs" data-testid="newconsult-mode-new" onClick={() => setMode("new")}>New customer</Button>
        </div>
        {mode === "new" ? (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="nc-name" placeholder="Customer name *" value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} />
              <Input data-testid="nc-phone" placeholder="Phone / WhatsApp with country code *" value={nc.phone} onChange={(e) => setNc({ ...nc, phone: e.target.value })} />
              <Input data-testid="nc-email" placeholder="Email (optional)" value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} />
              <Select value={nc.product_code} onValueChange={(v) => setNc({ ...nc, product_code: v })}>
                <SelectTrigger data-testid="nc-product" className="h-9"><SelectValue placeholder="Product" /></SelectTrigger>
                <SelectContent>{products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={nc.counsellor_user_id} onValueChange={(v) => setNc({ ...nc, counsellor_user_id: v })}>
                <SelectTrigger data-testid="nc-counsellor" className="h-9"><SelectValue placeholder="Consultant" /></SelectTrigger>
                <SelectContent>{counsellors.map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="nc-date" type="date" className="h-9" value={ncDate} onChange={(e) => setNcDate(e.target.value)} />
            </div>
            {ncPicker.status !== "idle" && (
              <div className="space-y-1.5">
                <Label className="text-xs">Available slots (IST)</Label>
                <SlotPickerView picker={ncPicker} idPrefix="nc" compact gridClass="flex gap-1.5 flex-wrap max-h-28 overflow-y-auto"
                  buttonClass="h-7 text-xs" testIdFor={(sl) => `nc-slot-${sl.start}`} labelFor={(sl) => fmtISTTime(sl.start)} />
              </div>
            )}
            <Textarea data-testid="nc-notes" placeholder="Notes / basic details" value={nc.notes} onChange={(e) => setNc({ ...nc, notes: e.target.value })} />
            <p className="text-[11px] text-muted-foreground">Duplicate detection runs before creating the contact. A WhatsApp deep-link opens after booking to share appointment details.</p>
          </div>
        ) : (
        <div className="space-y-3">
          {!contact ? (
            <div className="space-y-1.5">
              <Label className="text-xs">Customer</Label>
              <Input data-testid="newconsult-search" placeholder="Name / phone / AP-…" value={q} onChange={(e) => setQ(e.target.value)} />
              <div className="space-y-1 max-h-36 overflow-y-auto">
                {results.map((r) => (
                  <button key={r.contact_id} data-testid={`newconsult-pick-${r.customer_id}`} onClick={() => setContact(r)}
                    className="w-full text-left p-2 rounded border text-sm hover:bg-muted/50">
                    {r.full_name} <span className="font-mono text-xs text-muted-foreground">{r.customer_id}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm">Customer: <b>{contact.full_name}</b> <button className="text-xs underline text-muted-foreground" onClick={() => setContact(null)}>change</button></p>
          )}
          {contact && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Opportunity (product pursuit)</Label>
                <Select value={oppId} onValueChange={setOppId}>
                  <SelectTrigger data-testid="newconsult-opp" className="h-9"><SelectValue placeholder="Pick opportunity" /></SelectTrigger>
                  <SelectContent>
                    {opps.map((o) => <SelectItem key={o.opportunity_id} value={o.opportunity_id}>{o.product_name} — {o.current_stage_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {opps.length === 0 && <p className="text-[11px] text-[hsl(var(--warning))]">No active opportunities — create a lead first.</p>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Type</Label>
                  <Select value={typeId} onValueChange={setTypeId}>
                    <SelectTrigger data-testid="newconsult-type" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {types.map((t) => <SelectItem key={t.appointment_type_id} value={t.appointment_type_id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Date</Label>
                  <Input data-testid="newconsult-date" type="date" className="h-9" value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
              </div>
              {picker.status !== "idle" && (
                <SlotPickerView picker={picker} idPrefix="newconsult" compact gridClass="flex gap-1.5 flex-wrap max-h-28 overflow-y-auto"
                  buttonClass="h-7 text-xs" testIdFor={(sl) => `newconsult-slot-${sl.start}`} labelFor={(sl) => fmtISTTime(sl.start)} />
              )}
            </>
          )}
        </div>
        )}
        <DialogFooter>
          {mode === "new" ? (
            <Button data-testid="nc-book" disabled={!nc.name || !nc.phone || !nc.product_code || !nc.counsellor_user_id || !ncPicker.slot || saving} onClick={bookNewCustomer}>
              {saving ? "Booking…" : "Create customer & book"}
            </Button>
          ) : (
          <Button data-testid="newconsult-book" disabled={!oppId || !picker.slot || saving} onClick={book}>
            {saving ? "Booking…" : "Book consultation"}
          </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function AppointmentsPage() {
  const location = useLocation();
  const returnTo = encodeURIComponent(location.pathname + location.search);
  const { hasCap } = useAuth();
  // 2026-09 P4 Round 2 (Problem 10) — queue AND every filter (counsellor,
  // product, tag, intent, date) live in the URL via useUrlState, so Back
  // from a customer opened off this page restores the exact prior state,
  // not just the queue tab. `page` stays local (this page's pagination is
  // a "Load More" accumulator, not a single numbered page — restoring the
  // exact accumulated scroll depth on Back is a known, noted residual gap).
  const [apptUrlState, setApptUrlState] = useUrlState({
    queue: "today", counsellor: "all", product: "all", tag: "all", intent: "all", date: "", outcome: "all",
  });
  const { queue, counsellor: fltCounsellor, product: fltProduct, tag: fltTag,
    intent: fltIntent, date: fltDate, outcome: fltOutcomeRaw } = apptUrlState;
  // The outcome filter belongs to the Completed tab only: leaving it clears the filter, and it is never sent for another queue, so a
  // Completed-only filter can never silently narrow Today / Upcoming / All after the employee switches tabs.
  const fltOutcome = queue === "completed" ? fltOutcomeRaw : "all";
  const setQueue = (q) => setApptUrlState(q === "completed" ? { queue: q } : { queue: q, outcome: "all" });
  const setFltOutcome = (v) => setApptUrlState({ outcome: v });
  const setFltCounsellor = (v) => setApptUrlState({ counsellor: v });
  const setFltProduct = (v) => setApptUrlState({ product: v });
  const setFltTag = (v) => setApptUrlState({ tag: v });
  const setFltIntent = (v) => setApptUrlState({ intent: v });
  const setFltDate = (v) => setApptUrlState({ date: v });
  const [items, setItems] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [counsellors, setCounsellors] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [tags, setTags] = useState([]);
  const [outcomeOptions, setOutcomeOptions] = useState(OUTCOME_FILTER_FALLBACK);
  const [loadError, setLoadError] = useState(false);
  const { includeTest, canInclude, setIncludeTest } = useIncludeTest();
  const PAGE_SIZE = 50;

  const fetchPage = useCallback((pageNum) => {
    const p = new URLSearchParams({ queue, page: String(pageNum), limit: String(PAGE_SIZE) });
    if (fltCounsellor !== "all") p.set("counsellor", fltCounsellor);
    if (fltProduct !== "all") p.set("product_code", fltProduct);
    if (fltTag !== "all") p.set("tag", fltTag);
    if (fltIntent !== "all") p.set("intent", fltIntent);
    if (fltDate) p.set("date", fltDate);
    if (queue === "completed" && fltOutcome !== "all") p.set("outcome", fltOutcome);
    if (includeTest) p.set("include_test", "true");          // R15 U4: TEST is hidden unless an administrator switches it on (the URL carries the choice)
    return api.get(`/appointments?${p.toString()}`).then((r) => r.data);
  }, [queue, fltCounsellor, fltProduct, fltTag, fltIntent, fltDate, fltOutcome, includeTest]);

  // Fresh load (queue/filter change, or an action that wants a clean re-fetch
  // of everything seen so far) always resets back to page 1 and replaces the
  // item list — it never appends.
  const load = useCallback(() => {
    setPage(1);
    setLoadError(false);
    fetchPage(1).then((d) => {
      setItems(d.items);
      setHasMore(d.has_more);
      setTotal(d.total);
    }).catch(() => { setItems([]); setHasMore(false); setTotal(0); setLoadError(true); });
  }, [fetchPage]);

  const loadMore = () => {
    setLoadingMore(true);
    const next = page + 1;
    fetchPage(next).then((d) => {
      setItems((prev) => [...(prev || []), ...d.items]);
      setHasMore(d.has_more);
      setTotal(d.total);
      setPage(next);
    }).finally(() => setLoadingMore(false));
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // canonical Products — new products appear automatically
    api.get("/products").then((r) => setProducts((r.data || []).filter((x) => x.active !== false))).catch(() => {});
    api.get("/tags").then((r) => setTags(r.data.tags || [])).catch(() => {});
    api.get("/appointments/outcome-filters").then((r) => { if (Array.isArray(r.data) && r.data.length) setOutcomeOptions(r.data); }).catch(() => {});
    if (hasCap("booking.assign")) {
      api.get("/users").then((r) => setCounsellors(r.data.users.filter((u) => u.is_active && ["counsellor", "supervisor", "manager", "admin"].includes(u.role))));
    }
  }, [hasCap]);

  const assign = async (appt, userId) => {
    // Optimistic-concurrency: tell the backend what THIS row was loaded
    // with so its compare-and-swap checks against the right baseline. A
    // real two-admin race still returns 409; omitting this (the old bug)
    // made the CAS compare every assigned consultation against
    // "Unassigned" and always false-conflict.
    if (userId === (appt.counsellor_user_id || null)) return; // harmless no-op
    try {
      await api.patch(`/appointments/${appt.appointment_id}/assign`, {
        counsellor_user_id: userId,
        expected_current_counsellor_user_id: appt.counsellor_user_id || null,
      });
      toast.success("Counsellor assigned");
      load();
    } catch (e) { toast.error(errDetail(e)); if (e?.response?.status === 409) load(); }
  };

  return (
    <div className="space-y-4" data-testid="appointments-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Consultations / Meetings</h1>
          <p className="text-sm text-muted-foreground">SalesOS is the canonical source — Google Calendar/Meet sync runs when connected, with manual Meet-link fallback always available.</p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Select value={fltProduct} onValueChange={setFltProduct}>
            <SelectTrigger data-testid="consult-product-filter" className="h-9 w-44 sm:w-52 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All products</SelectItem>
              {products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fltCounsellor} onValueChange={setFltCounsellor}>
            <SelectTrigger data-testid="consult-counsellor-filter" className="h-9 w-44 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All counsellors</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {counsellors.map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fltTag} onValueChange={setFltTag}>
            <SelectTrigger data-testid="consult-tag-filter" className="h-9 w-36 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tags</SelectItem>
              {tags.map((t) => <SelectItem key={t.tag_id} value={t.tag_id}>{t.name} ({t.group})</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fltIntent} onValueChange={setFltIntent}>
            <SelectTrigger data-testid="consult-intent-filter" className="h-9 w-36 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All intent</SelectItem>
              {["high_intent", "warm", "cold", "not_interested", "not_eligible"].map((i) =>
                <SelectItem key={i} value={i}>{i.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <Input data-testid="consult-date-filter" type="date" className="h-9 w-36 text-xs"
              value={fltDate} onChange={(e) => setFltDate(e.target.value)} />
            {fltDate && (
              <Button data-testid="consult-date-filter-clear" variant="ghost" size="sm" className="h-9 px-2 text-xs"
                onClick={() => setFltDate("")}>Clear</Button>
            )}
          </div>
          {queue === "completed" && (
            <div className="flex items-center gap-1.5" data-testid="consult-outcome-filter-group">
              <Label className="text-[10px] uppercase text-muted-foreground whitespace-nowrap">Commercial outcome</Label>
              <Select value={fltOutcome} onValueChange={setFltOutcome}>
                <SelectTrigger data-testid="consult-outcome-filter" className="h-9 w-52 text-xs" aria-label="Commercial outcome"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {outcomeOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <IncludeTestToggle includeTest={includeTest} canInclude={canInclude} onChange={setIncludeTest} testId="consult-include-test" />
          <Button data-testid="newconsult-button" className="h-9" onClick={() => setNewOpen(true)}>
            <CalendarClock className="h-4 w-4 mr-1.5" /> New consultation
          </Button>
        </div>
      </div>

      <Tabs value={queue} onValueChange={setQueue}>
        <TabsList className="flex-wrap h-auto">
          {QUEUES.map((q) => <TabsTrigger key={q.key} data-testid={`appt-queue-${q.key}`} value={q.key}>{q.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <Card>
        {!items ? <div className="p-4 space-y-2" data-testid="appt-loading">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
          : loadError ? <ErrorState testId="appt-error" title="Couldn't load consultations" onRetry={load} />
          : items.length === 0 ? (fltOutcome !== "all"
            ? <EmptyState icon={CalendarClock} title="No completed consultations with this commercial outcome"
                hint={`Nothing matches “${(outcomeOptions.find((o) => o.value === fltOutcome) || {}).label || fltOutcome}” with the other filters applied. Change the outcome, product, consultant or date, or clear the outcome filter.`} />
            : <EmptyState icon={CalendarClock} title="No appointments in this queue" />)
            : (
              <>
                {/* Mobile: card list */}
                <div className="md:hidden divide-y" data-testid="appt-card-list">
                  {items.map((a) => {
                    const active = ["booked", "rescheduled"].includes(a.status);
                    return (
                      <div key={a.appointment_id} className="p-3 space-y-1.5" data-testid={`appt-card-${a.appointment_id}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium">{fmtIST(a.scheduled_start, { year: true })}</span>
                          <ApptBadge status={a.status} />
                        </div>
                        <Link to={`/customers/${a.contact_id}?return_to=${returnTo}`} className="flex items-center gap-2 hover:underline">
                          <span className="text-sm font-medium">{a.contact_name}</span>
                          <TestBadge show={a.is_test_record} />
                        </Link>
                        <div className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono">{a.customer_id}</span>
                          <span>· {a.appointment_type_name}</span>
                          {a.product_code && <Badge variant="outline" className="text-[9px]">{(products.find((p) => p.code === a.product_code) || {}).name || a.product_code}</Badge>}
                        </div>
                        <div className="text-xs text-muted-foreground">Counsellor: {a.counsellor_name || "Unassigned"}</div>
                        {OUTCOME_QUEUES.includes(queue) && (
                          <div className="text-xs flex items-center gap-1.5" data-testid={`appt-card-outcome-wrap-${a.appointment_id}`}>
                            <span className="text-muted-foreground">Commercial outcome:</span>
                            <OutcomeBadge outcome={a.commercial_outcome} testId={`appt-card-outcome-${a.appointment_id}`} />
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <IntentBadge category={a.intent} />
                          <TagsEditor entity="opportunity" recordId={a.opportunity_id} tags={a.tags || []} />
                        </div>
                        {active && (
                          <div className="pt-1">
                            <ConsultationActions appt={a} counsellors={counsellors} onSaved={load} canAddMeetLink={hasCap("booking.manage")} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Desktop: table */}
                <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When (IST)</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="hidden md:table-cell">Type</TableHead>
                    <TableHead>Counsellor</TableHead>
                    <TableHead className="hidden lg:table-cell">Tags</TableHead>
                    <TableHead className="hidden lg:table-cell">Intent</TableHead>
                    {OUTCOME_QUEUES.includes(queue) && <TableHead data-testid="appt-outcome-header">Commercial outcome</TableHead>}
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden lg:table-cell">Sync</TableHead>
                    <TableHead className="text-center w-56">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((a) => {
                    const active = ["booked", "rescheduled"].includes(a.status);
                    return (
                      <TableRow key={a.appointment_id} data-testid={`appt-row-${a.appointment_id}`}>
                        <TableCell className="text-sm font-medium">{fmtIST(a.scheduled_start, { year: true })}</TableCell>
                        <TableCell>
                          <Link to={`/customers/${a.contact_id}?return_to=${returnTo}`} className="flex items-center gap-2 hover:underline">
                            <span className="text-sm font-medium">{a.contact_name}</span>
                            <TestBadge show={a.is_test_record} />
                          </Link>
                          <span className="font-mono text-[11px] text-muted-foreground">{a.customer_id}</span>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                          {a.appointment_type_name}
                          {a.product_code && <div><Badge variant="outline" className="text-[9px] mt-0.5" data-testid={`appt-product-${a.appointment_id}`}>{(products.find((p) => p.code === a.product_code) || {}).name || a.product_code}</Badge></div>}
                        </TableCell>
                        <TableCell>
                          {hasCap("booking.assign") && active ? (
                            <Select value={a.counsellor_user_id || ""} onValueChange={(v) => assign(a, v)}>
                              <SelectTrigger data-testid={`appt-assign-${a.appointment_id}`} className="h-8 w-40">
                                <SelectValue placeholder="Assign…" />
                              </SelectTrigger>
                              <SelectContent>
                                {counsellors.map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-sm">{a.counsellor_name || <span className="text-muted-foreground">Unassigned</span>}</span>
                          )}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <TagsEditor entity="opportunity" recordId={a.opportunity_id} tags={a.tags || []} />
                        </TableCell>
                        <TableCell className="hidden lg:table-cell"><IntentBadge category={a.intent} /></TableCell>
                        {OUTCOME_QUEUES.includes(queue) && (
                          <TableCell><OutcomeBadge outcome={a.commercial_outcome} testId={`appt-outcome-${a.appointment_id}`} /></TableCell>
                        )}
                        <TableCell><ApptBadge status={a.status} /></TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {active ? <CalSyncBadge status={a.calendar_sync_status} /> : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="w-56">
                          {active && (
                            <div className="flex justify-center">
                              <ConsultationActions appt={a} counsellors={counsellors} onSaved={load} canAddMeetLink={hasCap("booking.manage")} compact />
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
                </div>
                <div className="flex items-center justify-between p-3 border-t">
                  <span className="text-xs text-muted-foreground" data-testid="appt-count">
                    Showing {items.length} of {total}
                    {fltOutcome !== "all" && <> · Commercial outcome: {(outcomeOptions.find((o) => o.value === fltOutcome) || {}).label || fltOutcome}</>}
                  </span>
                  {hasMore && (
                    <Button data-testid="appt-load-more" variant="outline" size="sm" disabled={loadingMore} onClick={loadMore}>
                      {loadingMore ? "Loading…" : "Load more"}
                    </Button>
                  )}
                </div>
              </>
            )}
      </Card>

      <NewConsultationDialog open={newOpen} setOpen={setNewOpen} onBooked={load} />
    </div>
  );
}
